import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToasterService } from '@shared/services/toaster.service';

@Component({
    selector: 'app-toaster',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div class="sb-wrap" aria-live="polite" aria-atomic="true">
      @for (toast of toaster.toasts(); track toast.id) {
      <div class="sb" [ngClass]="toast.type" [style.animation-delay]="$index * 80 + 'ms'">

        <!-- Icon -->
        <div class="sb-icon">
          @switch (toast.type) {
            @case ('success') {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
            }
            @case ('error') {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                   stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M15 9l-6 6M9 9l6 6"/>
              </svg>
            }
            @case ('warning') {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 3L2.5 19h19L12 3z"/>
                <path d="M12 9v4M12 16h.01"/>
              </svg>
            }
            @default {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                   stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4M12 8h.01"/>
              </svg>
            }
          }
        </div>

        <!-- Message -->
        <span class="sb-msg">{{ toast.message }}</span>

        <!-- Close -->
        <button class="sb-close" type="button" aria-label="Dismiss" (click)="dismiss(toast.id)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
               stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>

        <!-- Progress -->
        <div class="sb-prog-track"><div class="sb-prog"></div></div>
      </div>
      }
    </div>
  `,
    styles: [`
    /* Container – bottom-center */
    .sb-wrap {
      position: fixed;
      left: 1rem;
      right: 1rem;
      bottom: 1.5rem;
      z-index: 999999;
      display: flex;
      flex-direction: column-reverse;
      align-items: stretch;
      gap: 0.5rem;
      pointer-events: none;
    }
    @media (min-width: 640px) {
      .sb-wrap {
        left: 50%;
        right: auto;
        transform: translateX(-50%);
        width: 25rem;
      }
    }

    /* Card */
    .sb {
      position: relative;
      display: flex;
      align-items: center;
      gap: 0.625rem;
      padding: 0.8rem 0.875rem;
      border-radius: 0.875rem;
      overflow: hidden;
      pointer-events: auto;
      background: #fff;
      border: 1px solid #e8e0ec;
      color: #1a0a12;
      box-shadow: 0 8px 24px -6px rgba(39,12,27,0.14), 0 2px 6px -2px rgba(0,0,0,0.06);
      animation: sb-in 0.32s cubic-bezier(0.16,1,0.3,1) both;
      transform: translateY(0.75rem) scale(0.98);
      opacity: 0;
    }
    :host-context(.dark) .sb {
      background: #1c0e16;
      border-color: rgba(255,255,255,0.09);
      color: #f0e6ea;
      box-shadow: 0 10px 32px -8px rgba(0,0,0,0.55), 0 2px 8px -2px rgba(0,0,0,0.3);
    }

    /* Coloured left edge */
    .sb::after {
      content: '';
      position: absolute;
      top: 10%;
      bottom: 10%;
      left: 0;
      width: 3px;
      border-radius: 0 3px 3px 0;
    }
    .sb.success::after { background: #10b981; }
    .sb.error::after   { background: #ef4444; }
    .sb.warning::after { background: #f59e0b; }
    .sb.info::after    { background: #bd458e; }

    /* Icon circle */
    .sb-icon {
      flex-shrink: 0;
      width: 1.875rem;
      height: 1.875rem;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 0.25rem;
    }
    .sb-icon svg { width: 1rem; height: 1rem; }

    .sb.success .sb-icon { background: #d1fae5; color: #059669; }
    .sb.error   .sb-icon { background: #fee2e2; color: #dc2626; }
    .sb.warning .sb-icon { background: #fef3c7; color: #d97706; }
    .sb.info    .sb-icon { background: #fce7f3; color: #9f2e73; }

    :host-context(.dark) .sb.success .sb-icon { background: rgba(16,185,129,0.18); color: #34d399; }
    :host-context(.dark) .sb.error   .sb-icon { background: rgba(239,68,68,0.18); color: #f87171; }
    :host-context(.dark) .sb.warning .sb-icon { background: rgba(245,158,11,0.18); color: #fbbf24; }
    :host-context(.dark) .sb.info    .sb-icon { background: rgba(189,69,142,0.18); color: #e789c3; }

    /* Message */
    .sb-msg {
      flex: 1;
      min-width: 0;
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 1.45;
      overflow-wrap: anywhere;
    }

    /* Close */
    .sb-close {
      flex-shrink: 0;
      width: 1.625rem;
      height: 1.625rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      border-radius: 50%;
      background: transparent;
      color: inherit;
      opacity: 0.3;
      cursor: pointer;
      transition: opacity 0.15s, background 0.15s;
    }
    .sb-close:hover { opacity: 0.8; background: rgba(0,0,0,0.06); }
    :host-context(.dark) .sb-close:hover { background: rgba(255,255,255,0.1); }
    .sb-close svg { width: 0.8rem; height: 0.8rem; }

    /* Progress */
    .sb-prog-track {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
    }
    .sb-prog {
      height: 100%;
      width: 100%;
      transform-origin: left;
      animation: sb-prog 3s linear forwards;
    }
    .sb.success .sb-prog { background: #10b981; }
    .sb.error   .sb-prog { background: #ef4444; }
    .sb.warning .sb-prog { background: #f59e0b; }
    .sb.info    .sb-prog { background: #bd458e; }

    /* Animations */
    @keyframes sb-in {
      to { transform: translateY(0) scale(1); opacity: 1; }
    }
    @keyframes sb-prog {
      from { transform: scaleX(1); }
      to   { transform: scaleX(0); }
    }

    @media (prefers-reduced-motion: reduce) {
      .sb { animation: none !important; transform: none !important; opacity: 1 !important; }
      .sb-prog { animation: none !important; }
    }
  `],
})
export class ToasterComponent {
    constructor(public toaster: ToasterService) { }

    dismiss(id: number): void {
        this.toaster.dismiss(id);
    }
}
